"use client";

import React, { useRef, useState } from "react";
import {
    Rocket,
    CheckCircle2,
    Library,
    Clock,
    Users,
    BookOpen,
    PenTool,
    TrendingUp,
    Award,
    ChevronRight,
    ArrowRight,
    Volume2,
    VolumeX
} from "lucide-react";

import PricingSection from "./PricingSection";
import ReferralSection from "./ReferralSection";
import FAQSection from "./FAQSection";

export default function SapieducaLandingPage() {
    const videoRef = useRef<HTMLVideoElement>(null);
    const [isMuted, setIsMuted] = useState(true);

    const toggleMute = () => {
        if (videoRef.current) {
            videoRef.current.muted = !videoRef.current.muted;
            setIsMuted(videoRef.current.muted);
        }
    };

    const heroImage = "/assets/images/hero_owl.jpg";
    const heroVideo = "/assets/videos/hero_video.mp4";
    const logoIcon = "/assets/images/logo_icon_new.png";
    const ctaLink = "https://app.sapieduca.com.br/ia";

    const handleCTAClick = () => {
        window.location.href = ctaLink;
    };


    return (
        <div className="min-h-screen bg-white font-sans text-slate-900 selection:bg-blue-100 selection:text-[#3266cc]">

            {/* 1. HEADER */}
            <header className="sticky top-0 z-50 w-full bg-white/90 backdrop-blur-lg border-b border-slate-100">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-20 relative">
                        <div className="flex-shrink-0 flex items-center gap-3">
                            <div className="w-12 h-12 flex items-center justify-center">
                                <img
                                    src={logoIcon}
                                    alt="Sapieduca Logo"
                                    className="w-full h-full object-contain"
                                />
                            </div>
                        </div>
                        <div className="hidden md:flex items-center gap-8 absolute left-1/2 -translate-x-1/2">
                            <a href="#RAG" className="text-sm font-semibold text-slate-600 hover:text-[#3266cc] transition-colors no-underline">Sobre</a>
                            <a href="#Mission" className="text-sm font-semibold text-slate-600 hover:text-[#3266cc] transition-colors no-underline">Impacto</a>
                            <a href="#Pricing" className="text-sm font-semibold text-slate-600 hover:text-[#3266cc] transition-colors no-underline">Planos</a>
                            <a href="#FAQ" className="text-sm font-semibold text-slate-600 hover:text-[#3266cc] transition-colors no-underline">FAQ</a>
                            <a href="#Pricing" className="text-sm font-semibold text-slate-600 hover:text-[#3266cc] transition-colors no-underline">Para Escolas</a>
                        </div>
                        <div>
                            <button
                                onClick={handleCTAClick}
                                className="inline-flex items-center justify-center px-6 py-2.5 border border-transparent text-sm font-bold rounded-full text-white bg-[#3266cc] hover:bg-[#2855b0] transition-all shadow-md hover:shadow-lg active:scale-95"
                            >
                                Entrar
                            </button>
                        </div>
                    </div>
                </div>
            </header>

            {/* 2. HERO SECTION */}
            <section className="relative pt-12 pb-24 lg:pt-20 lg:pb-32 overflow-hidden">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid lg:grid-cols-2 gap-12 items-center">

                        <div className="text-left relative z-10">

                            <h1 className="text-5xl md:text-5xl font-black tracking-tight text-slate-900 mb-8 leading-[1.1]">
                                Planeje <span className="text-[#3266cc]">aulas incríveis</span> com foco em <span className="text-[#3266cc]">diversidade e inclusão</span>
                            </h1>

                            <p className="text-xl text-slate-600 leading-relaxed mb-10 max-w-xl">
                                Crie planos de aula, provas e outros materiais em segundos. A inteligência da Sapieduca adapta o conteúdo ao contexto da sua escola, garantindo foco em inclusão, diversidade e alinhamento total à BNCC.
                            </p>

                            <div className="flex flex-col sm:flex-row gap-4 items-start">
                                <button
                                    onClick={handleCTAClick}
                                    className="w-full sm:w-auto px-8 py-4 text-lg font-bold rounded-full text-white bg-[#3266cc] hover:bg-[#2855b0] shadow-xl shadow-blue-500/30 transition-all flex items-center justify-center gap-2 group"
                                >
                                    Criar minha primeira aula grátis
                                    <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                                </button>
                            </div>
                            <p className="mt-6 text-sm text-slate-500 flex items-center gap-2 font-medium">
                                <CheckCircle2 className="w-4 h-4 text-teal-500" /> Conteúdo pronto para usar na sua próxima aula.
                            </p>
                        </div>

                        <div className="relative order-first lg:order-last max-w-sm mx-auto lg:max-w-none">
                            <div className="absolute -inset-4 bg-blue-100 rounded-[2rem] blur-3xl opacity-30 -z-10 animate-pulse"></div>
                            <div className="relative rounded-[2.5rem] overflow-hidden shadow-2xl border-8 border-white aspect-[13/15] max-h-[480px] flex items-center justify-center bg-slate-900">
                                <video
                                    ref={videoRef}
                                    src={heroVideo}
                                    autoPlay
                                    muted
                                    loop
                                    playsInline
                                    className="w-full h-full object-cover object-[center_28.5%] transform hover:scale-105 transition-transform duration-1000"
                                />
                                <button
                                    onClick={toggleMute}
                                    className="absolute bottom-4 right-4 z-20 p-3 rounded-full bg-slate-900/80 hover:bg-slate-900 text-white backdrop-blur-md transition-all shadow-md active:scale-95 focus:outline-none"
                                    title={isMuted ? "Ativar som" : "Desativar som"}
                                >
                                    {isMuted ? (
                                        <VolumeX className="w-5 h-5 text-white" />
                                    ) : (
                                        <Volume2 className="w-5 h-5 text-white" />
                                    )}
                                </button>
                            </div>
                        </div>

                    </div>
                </div>
            </section>

            {/* 3. FAIXA DE AUTORIDADE */}
            <section className="py-16 bg-slate-50 border-y border-slate-100">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <p className="text-center text-xs font-bold text-slate-400 uppercase tracking-[0.2em] mb-12">
                        POR ONDE PASSAMOS
                    </p>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-8 items-center justify-items-center">
                        <a href="https://www.itausocial.org.br/noticias/esta-no-ar-a-lista-dos-selecionados-do-edital-inteligencia-artificial-para-a-educacao/" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center grayscale opacity-60 hover:grayscale-0 hover:opacity-100 transition-all h-28 w-40 mix-blend-multiply">
                            <img src="/assets/images/logo_itau.png" alt="Fundação Itaú" className="max-h-20 max-w-full object-contain" />
                        </a>
                        <a href="https://fiepb.com.br/senai/noticia/projeto-idealizado-pela-startup-sapientia-educacao-sera-desenvolvido-no-senai-pb-com-recursos-da-plataforma-de-inovacao-para-a-industria" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center grayscale opacity-60 hover:grayscale-0 hover:opacity-100 transition-all h-36 w-72 mix-blend-multiply">
                            <img src="/assets/images/logo_senai.png" alt="SENAI" className="max-h-28 max-w-full object-contain" />
                        </a>
                        <a href="https://garagem.bndes.gov.br/comunidade/" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center grayscale opacity-60 hover:grayscale-0 hover:opacity-100 transition-all h-28 w-44 mix-blend-multiply">
                            <img src="/assets/images/logo_bndes.png" alt="BNDES" className="max-h-16 max-w-full object-contain" />
                        </a>
                        <a href="https://paraiba.pb.gov.br/noticias/com-apoio-do-governo-da-paraiba-professor-cria-programa-de-monitoramento-estudantil-que-ganha-destaque-na-finlandia" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center grayscale opacity-60 hover:grayscale-0 hover:opacity-100 transition-all h-28 w-52 mix-blend-multiply">
                            <img src="/assets/images/logo_paraiba.png" alt="Governo da Paraíba" className="max-h-20 max-w-full object-contain" />
                        </a>
                        <a href="https://hundred.org/en/innovations/sapieduca-sapientia" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center transition-all h-28 w-40 mix-blend-multiply group">
                            <div
                                className="w-full h-16 bg-slate-500 group-hover:bg-[#c30f96] opacity-60 group-hover:opacity-100 transition-all duration-300"
                                style={{
                                    WebkitMaskImage: 'url(/assets/images/logo_hundred_new.png)',
                                    WebkitMaskSize: 'contain',
                                    WebkitMaskRepeat: 'no-repeat',
                                    WebkitMaskPosition: 'center',
                                    maskImage: 'url(/assets/images/logo_hundred_new.png)',
                                    maskSize: 'contain',
                                    maskRepeat: 'no-repeat',
                                    maskPosition: 'center',
                                }}
                            />
                        </a>
                    </div>
                </div>
            </section>

            {/* 4. SEÇÃO "COMO FUNCIONA / RAG" */}
            <section id="RAG" className="py-24 bg-white relative">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <div className="inline-block p-3 rounded-2xl bg-[#3266cc]/5 text-[#3266cc] mb-6">
                            <Library className="w-10 h-10" />
                        </div>
                        <h2 className="text-4xl md:text-4xl font-black text-slate-900 mb-6 tracking-tight">
                            Conteúdos pedagógicos com a sua identidade e alinhados à sua escola
                        </h2>
                    </div>
                    <div className="grid lg:grid-cols-5 gap-8 items-center">
                        <div className="lg:col-span-3">
                            <p className="text-xl md:text-2xl text-slate-600 leading-relaxed mb-8">
			    	Nossa tecnologia utiliza o contexto da sua escola, suas preferências e nossa base de conhecimento curada para criar materiais sob medida, entregando resultados seguros e pedagogicamente embasados.
                            </p>
                            <div className="space-y-4">
                                <div className="flex items-start gap-4 p-4 rounded-xl bg-slate-50 border border-slate-100">
                                    <CheckCircle2 className="w-6 h-6 text-teal-500 flex-shrink-0 mt-0.5" />
                                    <p className="text-slate-700 font-medium">Personalização contínua que aprende com você, respeita as suas preferências e o seu estilo de ensino.</p>
                                </div>
                                <div className="flex items-start gap-4 p-4 rounded-xl bg-slate-50 border border-slate-100">
                                    <CheckCircle2 className="w-6 h-6 text-teal-500 flex-shrink-0 mt-0.5" />
                                    <p className="text-slate-700 font-medium">Alinhamento institucional com materiais gerados rigorosamente dentro das diretrizes da sua escola.
</p>
                                </div>
                            </div>
                        </div>
                        <div className="lg:col-span-2 bg-gradient-to-br from-[#3266cc] to-blue-700 p-8 rounded-[2rem] shadow-2xl text-white">
                            <h4 className="text-xl font-bold mb-4">Por que os professores confiam?</h4>
                            <p className="text-blue-50 leading-relaxed italic opacity-90">
                                "Como garantimos que o conteúdo siga exatamente as diretrizes da sua instituição, você ganha horas de tempo livre e entra em sala de aula com a segurança de utilizar um material impecável, coerente e pronto para uso."
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* 5. OS 4 PILARES DA IA */}
            <section className="py-24 bg-slate-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-20">
                        <h2 className="text-4xl font-black text-slate-900 mb-4 tracking-tight">Por que a Sapieduca IA?</h2>
                        <div className="w-24 h-1.5 bg-[#3266cc] mx-auto rounded-full"></div>
                    </div>

                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        <Card pilar="Agilidade no planejamento" icon={<Clock />} color="#3266cc" text="Reduza em até 70% o tempo gasto com burocracias. Gere materiais em segundos, mantendo a sua identidade e com a certeza do alinhamento à BNCC." />
                        <Card pilar="Identidade e representatividade" icon={<Users />} color="#3266cc" text="Cumpra a Lei nº 11.645 com segurança e embasamento. A IA sugere abordagens autênticas sobre história e cultura afro-brasileira e indígena, combatendo estereótipos em sala de aula." />
                        <Card pilar="Inclusão na prática" icon={<BookOpen />} color="#3266cc" text="Adapte atividades e avaliações rapidamente para diferentes necessidades de aprendizagem. Tudo de forma ética e alinhada à Política Nacional de Educação Especial Inclusiva." />
                        <Card pilar="Autoria docente" icon={<PenTool />} color="#3266cc" text="A IA é sua assistente, não sua substituta. Você no comando: ajuste, edite e garanta que cada plano de aula gerado reflita a sua voz e o seu estilo único de ensino." />
                    </div>
                </div>
            </section>

            {/* 6. NOSSA JORNADA (EVOLUTION) */}
            <section id="Mission" className="py-24 bg-[#3266cc] text-white overflow-hidden relative">
                <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-white opacity-[0.03] rounded-full -mr-48 -mt-48"></div>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">

                    <div className="text-center max-w-3xl mx-auto mb-20">
                        <h2 className="text-4xl md:text-5xl font-black mb-6">Tecnologia criada por quem conhece o chão da escola</h2>
                        <p className="text-xl text-blue-100 opacity-90">
                            De uma plataforma validada por milhares de alunos na rede pública da Paraíba à Inteligência Artificial apoiada pela Fundação Itaú. Nossa evolução sempre teve um único foco: transformar a educação real.
                        </p>
                    </div>

                    <div className="relative">
                        {/* Timeline logic visual */}
                        <div className="hidden lg:block absolute left-1/2 top-0 bottom-0 w-0.5 bg-blue-400/30 -translate-x-1/2"></div>

                        <div className="space-y-12 lg:space-y-0">
                            {/* Etapa 1: O Legado */}
                            <div className="lg:flex items-center justify-between">
                                <div className="lg:w-[45%] bg-white/10 hover:bg-white/15 transition-colors backdrop-blur-md p-10 rounded-[2.5rem] border border-white/10 shadow-xl">
                                    <span className="text-xs font-black uppercase tracking-widest text-blue-200 block mb-4">2019 - 2023 | ESCOLAS PÚBLICAS DA PARAÍBA</span>
                                    <h3 className="text-3xl font-bold mb-6">Plataforma Sapieduca</h3>
                                    <ul className="space-y-4">
                                        <li className="flex items-center gap-3 text-lg"><Users className="w-5 h-5 text-blue-100" />+3 mil estudantes impactados</li>
                                        <li className="flex items-center gap-3 text-lg"><Award className="w-5 h-5 text-blue-100" />64% relataram maior protagonismo</li>
                                        <li className="flex items-center gap-3 text-lg"><TrendingUp className="w-5 h-5 text-blue-100" />Facilidade no acesso a dados de aprendizagem</li>
                                    </ul>
                                </div>
                                <div className="hidden lg:flex w-12 h-12 bg-white rounded-full items-center justify-center relative z-20 shadow-xl">
                                    <ArrowRight className="text-[#3266cc] w-6 h-6" />
                                </div>
                                <div className="lg:w-[45%] mt-8 lg:mt-0 p-10">
                                    <p className="text-xl font-medium leading-relaxed italic opacity-80">
                                        "Validada na ECIT Monsenhor José da Silva Coutinho, na cidade de Esperança, Paraíba, entre 2019 e 2023, a plataforma apresentou excelentes resultados, tornando-se uma ferramenta fundamental para o engajamento estudantil durante as aulas remotas decorrentes da COVID-19."
                                    </p>
                                </div>
                            </div>

                            {/* Etapa 2: A Evolução */}
                            <div className="lg:flex items-center justify-between lg:mt-24">
                                <div className="lg:w-[45%] order-last lg:order-first p-10 text-right">
                                    <p className="text-xl font-medium leading-relaxed italic opacity-80">
                                        “Em parceria com a Fundação Itaú, por meio do edital Inteligência Artificial para a Educação, desenvolvemos uma tecnologia ágil, inclusiva e personalizável, voltada ao contexto das salas de aula da Educação Básica em todo o Brasil, com atenção especial às escolas indígenas e quilombolas.”
                                    </p>
                                </div>
                                <div className="hidden lg:flex w-12 h-12 bg-white rounded-full items-center justify-center relative z-20 shadow-xl">
                                    <Rocket className="text-[#3266cc] w-6 h-6" />
                                </div>
                                <div className="lg:w-[45%] bg-gradient-to-br from-teal-500 to-[#3266cc] p-10 rounded-[2.5rem] border border-white/20 shadow-2xl relative overflow-hidden group">
                                    <div className="absolute top-0 right-0 p-4 opacity-10"><Rocket className="w-32 h-32 rotate-12" /></div>
                                    <span className="text-xs font-black uppercase tracking-widest text-teal-100 block mb-4">2025 - HOJE | PROFESSORES DA EDUCAÇÃO BÁSICA</span>
                                    <h3 className="text-3xl font-bold mb-6">Sapieduca IA</h3>
                                    <p className="text-xl leading-relaxed mb-6">
                                        Unimos nossa vivência pedagógica com a inteligência artificial para criar uma assistente que planeja aulas ágeis, inclusivas e alinhadas à sua realidade.
                                    </p>
                                    <button onClick={handleCTAClick} className="px-6 py-3 bg-white text-[#2e6fc7] rounded-full font-black text-sm hover:bg-teal-50 transition-colors">
                                        Saber mais
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <PricingSection />
            <ReferralSection />
            <FAQSection />

            {/* 7. FOOTER / FINAL CTA - PREMIUM REDESIGN */}
            <section id="Register" className="py-24 bg-white relative overflow-hidden">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    <div className="bg-gradient-to-br from-[#3266cc] via-[#2855b0] to-[#1e3b75] rounded-[3rem] p-12 md:p-20 shadow-[0_40px_100px_-20px_rgba(50,102,204,0.3)] relative overflow-hidden group">
                        {/* Pattern Overlay */}
                        <div className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] pointer-events-none"></div>

                        {/* Animated Glow Elements */}
                        <div className="absolute -top-40 -right-40 w-96 h-96 bg-[#15b3a8] opacity-20 filter blur-[100px] group-hover:opacity-40 transition-opacity duration-1000"></div>
                        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-[#3266cc] opacity-20 filter blur-[100px] group-hover:opacity-40 transition-opacity duration-1000"></div>

                        <div className="relative z-10 text-center max-w-4xl mx-auto">
                            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 text-white text-xs font-black tracking-widest mb-8 uppercase">
                                <Rocket className="w-4 h-4" />
                                Comece Agora
                            </div>

                            <h2 className="text-4xl md:text-5xl font-black text-white mb-8 leading-tight tracking-tight">
                                Pronto para transformar sua <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-200 to-teal-200">rotina pedagógica</span>?
                            </h2>

                            <p className="text-xl md:text-2xl text-blue-100/80 mb-12 leading-relaxed font-medium">
                                Experimente a Sapieduca IA gratuitamente hoje mesmo. Crie planos de aula, avaliações e atividades alinhadas à BNCC e adaptadas para a inclusão em poucos segundos.
                            </p>

                            <div className="flex flex-col items-center">
                                <button
                                    onClick={handleCTAClick}
                                    className="px-12 py-6 rounded-full bg-white text-[#3266cc] font-black text-xl hover:bg-teal-50 transition-all shadow-2xl hover:shadow-white/10 active:scale-95 flex items-center justify-center gap-3 group/btn"
                                >
                                    Criar minha primeira aula grátis
                                    <ChevronRight className="w-6 h-6 group-hover/btn:translate-x-1 transition-transform" />
                                </button>
                            </div>

                            <div className="mt-12 flex flex-wrap justify-center gap-x-8 gap-y-3 text-sm font-semibold text-blue-200/80">
                                <span className="flex items-center gap-2">
                                    <CheckCircle2 className="w-4 h-4 text-teal-300" /> Agilidade no planejamento
                                </span>
                                <span className="flex items-center gap-2">
                                    <CheckCircle2 className="w-4 h-4 text-teal-300" /> 100% alinhado à BNCC
                                </span>
                                <span className="flex items-center gap-2">
                                    <CheckCircle2 className="w-4 h-4 text-teal-300" /> Foco em diversidade e inclusão
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Real Footer */}
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-24 pt-8 border-t border-slate-100 flex flex-col md:flex-row justify-between items-start gap-8">
                    <div className="flex flex-col items-start w-full md:w-auto">
                        <span className="font-extrabold text-3xl text-[#3266cc] tracking-tight whitespace-nowrap">Sapieduca IA</span>
                        <div className="flex flex-col gap-1 mt-2">
                            <a href="/termos" className="text-slate-400 text-sm hover:text-[#3266cc] transition-colors no-underline">Termos de Uso</a>
                            <a href="/privacidade" className="text-slate-400 text-sm hover:text-[#3266cc] transition-colors no-underline">Política de Privacidade</a>
                        </div>
                    </div>
                    <div className="text-slate-400 text-sm w-full text-right font-medium">
                        <p className="mb-1">&copy; 2026 Sapientia Educação</p>
                        <p>Feito com carinho e propósito para todos os educadores do Brasil. 🇧🇷</p>
                        <p><a href="mailto:contato@sapieduca.com.br" className="text-slate-400 hover:text-[#3266cc] transition-colors no-underline">contato@sapieduca.com.br</a></p>
                    </div>
                </div>
            </section>

        </div>
    );
}

function Card({ pilar, text, icon, color, textFull }: any) {
    return (
        <div className="bg-white p-8 rounded-[2.5rem] border border-slate-100 hover:border-transparent hover:shadow-2xl hover:shadow-blue-900/10 transition-all duration-300 group flex flex-col">
            <div
                className="w-14 h-14 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform"
                style={{ backgroundColor: `${color}15`, color: color }}
            >
                {React.cloneElement(icon, { size: 28 })}
            </div>
            <h3 className="text-xl font-black text-slate-900 mb-3">{pilar}</h3>
            <p className="text-slate-500 leading-relaxed font-medium">
                {text}
            </p>
        </div>
    );
}
